import { useEffect, useState, type FormEvent } from 'react';
import type { CreateServiceCategoryInput, ServiceCategory } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  useCreateServiceCategory,
  useDeleteServiceCategory,
  useUpdateServiceCategory,
} from './api.js';

interface ServiceCategoryFormModalProps {
  isOpen: boolean;
  /** Existing service category to edit, or null to create a new one. */
  serviceCategory: ServiceCategory | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  defaultRate: string;
  invoiceSku: string;
  qboClass: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  defaultRate: '',
  invoiceSku: '',
  qboClass: '',
  isActive: true,
};

function toFormState(category: ServiceCategory | null): FormState {
  if (!category) {
    return EMPTY_FORM;
  }
  return {
    name: category.name,
    defaultRate: category.defaultRate,
    invoiceSku: category.invoiceSku ?? '',
    qboClass: category.qboClass ?? '',
    isActive: category.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function ServiceCategoryFormModal({
  isOpen,
  serviceCategory: serviceCategoryProp,
  onClose,
}: ServiceCategoryFormModalProps): JSX.Element {
  const [serviceCategory, setServiceCategory] = useState<ServiceCategory | null>(
    serviceCategoryProp,
  );
  const isEdit = serviceCategory !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(serviceCategory));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateServiceCategory();
  const updateMutation = useUpdateServiceCategory();
  const deleteMutation = useDeleteServiceCategory();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setServiceCategory(serviceCategoryProp);
    }
  }, [isOpen, serviceCategoryProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(serviceCategory));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, serviceCategory, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save service category.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateServiceCategoryInput = {
      name: form.name.trim(),
      defaultRate: form.defaultRate.trim(),
      invoiceSku: form.invoiceSku.trim() ? form.invoiceSku.trim() : null,
      qboClass: form.qboClass.trim() ? form.qboClass.trim() : null,
      isActive: form.isActive,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: serviceCategory.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setServiceCategory(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!serviceCategory) {
      return;
    }
    deleteMutation.mutate(serviceCategory.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit service category' : 'New service category'}
        footer={
          <FormActions
            formId="service-category-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="service-category-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Name"
            required
            value={form.name}
            maxLength={200}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
          />

          <TextField
            label="Default hourly rate"
            required
            inputMode="decimal"
            value={form.defaultRate}
            placeholder="195.00"
            onChange={(e) => setForm((f) => ({ ...f, defaultRate: e.target.value }))}
            error={fieldErrors.defaultRate}
          />

          <TextField
            label="Invoice SKU"
            value={form.invoiceSku}
            maxLength={64}
            placeholder="QuickBooks item name for project time"
            onChange={(e) => setForm((f) => ({ ...f, invoiceSku: e.target.value }))}
            error={fieldErrors.invoiceSku}
          />

          <TextField
            label="QBO class"
            value={form.qboClass}
            maxLength={256}
            placeholder="QuickBooks class for project time"
            onChange={(e) => setForm((f) => ({ ...f, qboClass: e.target.value }))}
            error={fieldErrors.qboClass}
          />

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete service category"
        message={`Delete "${serviceCategory?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
